import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import type {
  ActorDTO,
  AgentConfig,
  BridgeView,
  CostSummary,
  DecisionDTO,
  DelegationPolicy,
  DeliveryPipelineReport,
  GitSnapshotDTO,
  GuardDecisionDTO,
  IdeaRowDTO,
  NodeDTO,
  RunDTO,
  RunEventDTO,
  EventDTO,
  IntentDTO,
  Metrics,
  Mode,
  MessageDTO,
  MissionView,
  Overview,
  ProjectDTO,
  ProjectStatus,
  ProjectSummary,
  RuleDTO,
  StatusReport,
  Runtime,
  Stage,
  TaskDetail,
  TaskDTO,
  ThreadDTO,
  WorkerSettingDTO,
  CodebaseMap,
  RoutineDTO,
  RoutineRunDTO,
} from "@roomate/core";
export type { WorkerSettingDTO };
import { MOCK_IMPORT_PREVIEW, type AgentImportDTO, type ImportPreviewDTO, type ImportSource } from "./agent-import.ts";
import type { WhereInfo } from "./where.ts";
import { guardQuery, type GuardFilters } from "./lib/guard.ts";
import { patchRuleLesson, type CorrectionDTO, type CorrectionRuleDTO, type LessonDTO } from "./lessons.ts";
import type { SandboxStatusDTO } from "./lib/sandbox.ts";
import { missionPath, type MissionFilters } from "./lib/mission.ts";
import { CHIEF_PAGE, chiefActionsSince, chiefQuery, startOfDay, type ChiefActionDTO, type ChiefActionFilters } from "./lib/chief.ts";
import type { RoutineListDTO } from "./lib/routines.ts";
import { STARTER_TEAM_FLAG, type OnboardingStatus, type StarterTeamResult } from "./lib/onboarding.ts";
import type { HookVerdicts, SkillDetailDTO, SkillFetchResult, SkillListItemDTO, SkillVersionDTO } from "./components/skills/skills.ts";

const TOKEN_KEY = "roomate.token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage blocked: the session lasts until reload
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

let serverOffsetMs = 0;

/** The hub's current time as best known (device clock plus the offset from the latest `Date` header). Use it, not `Date.now()`, to age hub timestamps. */
export function serverNow(): number {
  return Date.now() + serverOffsetMs;
}

/** Learns hub clock minus device clock from a response `Date` header (1 s resolution). A missing or invalid header changes nothing. */
export function noteServerDate(header: string | null, localNow = Date.now()): void {
  const t = header ? Date.parse(header) : NaN;
  if (!Number.isFinite(t)) return;
  const next = t - localNow;
  const moved = Math.abs(next - serverOffsetMs) >= 1000; // sub-second jitter from the header's resolution is not news
  serverOffsetMs = next;
  if (moved) for (const fn of offsetListeners) fn();
}

const offsetListeners = new Set<() => void>();

/** Calls `fn` whenever the learned offset changes; returns the unsubscribe function. */
export function onServerOffsetChange(fn: () => void): () => void {
  offsetListeners.add(fn);
  return () => void offsetListeners.delete(fn);
}

export async function api<T>(method: string, path: string, body?: unknown, token = getToken()): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      // A FormData body sets its own multipart content-type (with the boundary).
      ...(body !== undefined && !(body instanceof FormData) ? { "content-type": "application/json" } : {}),
    },
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
  });
  noteServerDate(res.headers.get("date"));
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? "error", err?.message ?? res.statusText);
  }
  return data as T;
}

/** Every query key starts with one of these, so live events can invalidate broadly but cheaply. */
export const keys = {
  me: ["me"] as const,
  overview: ["overview"] as const,
  bridge: ["bridge"] as const,
  mission: ["mission"] as const,
  git: (projectId: string) => ["git", projectId] as const,
  actors: ["actors"] as const,
  onboarding: ["onboarding"] as const,
  projects: ["projects"] as const,
  project: (id: string) => ["project", id] as const,
  /** GET /projects/:id/workers. The project id keeps a live event from refetching every project. */
  workers: (projectId: string) => ["workers", projectId] as const,
  /** GET /projects/:id/map. Invalidated together with that project's workers key. */
  map: (projectId: string) => ["map", projectId] as const,
  task: (id: string) => ["task", id] as const,
  intent: (id: string) => ["intent", id] as const,
  decisions: (scope: string) => ["decisions", scope] as const,
  rules: ["rules"] as const,
  events: (projectId?: string) => ["events", projectId ?? "all"] as const,
  metrics: ["metrics"] as const,
  runs: (scope: string) => ["runs", scope] as const,
  tasks: ["tasks", "all"] as const,
  run: (id: string) => ["run", id] as const,
  nodes: ["nodes"] as const,
  where: ["where"] as const,
  costs: ["costs"] as const,
  agentImport: (id: string) => ["agent-import", id] as const,
  agentImports: (actorId: string) => ["agent-imports", actorId] as const,
  threads: (filters: ThreadFilters = {}) => ["threads", filters] as const,
  messages: (filters: MessageFilters) => ["messages", filters] as const,
  /** Every guard query starts with "guard", so a live guard.* event refreshes all of them. */
  guard: (projectId: string, filters: GuardFilters) => ["guard", projectId, filters] as const,
  decision: (id: string) => ["decision", id] as const,
  // Under "lessons" so live.tsx can invalidate every lesson list with one root.
  projectLessons: (projectId: string) => ["lessons", "project", projectId] as const,
  runLessons: (runId: string) => ["lessons", "run", runId] as const,
  // Own root, so a live lesson.* or correction.* event refreshes them (live.tsx).
  correctionRules: (agentId: string) => ["corrections", "rules", agentId] as const,
  agentCorrections: (agentId: string) => ["corrections", "list", agentId] as const,
  // Under "routines" so live.tsx can invalidate the list, a routine and its runs with one root.
  routines: (projectId: string) => ["routines", "list", projectId] as const,
  routine: (id: string) => ["routines", "one", id] as const,
  routineRuns: (id: string) => ["routines", "runs", id] as const,
};

export type ThreadFilters = { projectId?: string; taskId?: string; actorId?: string };
export type MessageFilters = { threadId?: string; taskId?: string };

function withQuery(path: string, values: Record<string, string | undefined>): string {
  const query = new URLSearchParams(Object.entries(values).filter((entry): entry is [string, string] => entry[1] !== undefined));
  return query.size ? `${path}?${query}` : path;
}

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api<ActorDTO>("GET", "/me"), retry: false });
export const useOverview = () => useQuery({ queryKey: keys.overview, queryFn: () => api<Overview>("GET", "/overview") });
/**
 * GET /onboarding (first run). A hub without the route (404) reads as "no card"; any other failure is a real
 * error and retries as usual, so a transient 5xx does not hide the first-run card for good.
 */
export const useOnboarding = (refetchInterval: number | false = false) =>
  useQuery({
    queryKey: keys.onboarding,
    queryFn: () =>
      api<OnboardingStatus>("GET", "/onboarding").catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }),
    refetchInterval,
  });
/**
 * POST /onboarding/starter-team: one click creates the team. Its response carries the status after it, so the card moves on without a refetch.
 * This browser remembers the creation (STARTER_TEAM_FLAG) so the connect steps stay after a reload.
 */
export const useStarterTeam = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<StarterTeamResult>("POST", "/onboarding/starter-team"),
    onSuccess: (result) => {
      // The flag goes first: the status update below re-renders the card, which reads it.
      try {
        localStorage.setItem(STARTER_TEAM_FLAG, "1");
      } catch {
        // Storage can be off (private mode): the steps then last only until the next reload.
      }
      qc.setQueryData<OnboardingStatus | null>(keys.onboarding, result.onboarding);
      void qc.invalidateQueries({ queryKey: keys.actors });
    },
    // A 409 means the office already has agents: reread the status instead of showing a stale offer.
    onError: () => void qc.invalidateQueries({ queryKey: keys.onboarding }),
  });
};
export const useBridge = (projectId?: string, enabled = true) =>
  useQuery({ queryKey: projectId ? [...keys.bridge, projectId] : keys.bridge, queryFn: () => api<BridgeView>("GET", `/bridge${projectId ? `?projectId=${projectId}` : ""}`), enabled });
/** Backstop refetch of the `mission` query while the page is visible. */
export const MISSION_REFETCH_MS = 60_000;

/**
 * GET /mission (mission control §4.3): the agent cards, filtered server-side. Live events invalidate it
 * (live.tsx); the 60 s refetch is the backstop against a missed update, and it pauses while the tab is
 * hidden (docs/specs/mission-control.md §4.4).
 */
export const useMission = (filters: MissionFilters, enabled = true) =>
  useQuery({
    queryKey: [...keys.mission, filters] as const,
    queryFn: () => api<MissionView>("GET", missionPath(filters)),
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: MISSION_REFETCH_MS,
    refetchIntervalInBackground: false,
  });
export const useGitSnapshot = (projectId: string | null) =>
  useQuery({
    queryKey: keys.git(projectId ?? ""),
    queryFn: () => api<GitSnapshotDTO | null>("GET", `/projects/${projectId}/git`),
    enabled: !!projectId,
    // The hub only has a snapshot of what a node last reported (every 60s, no push event) — poll
    // rather than relying on WS invalidation, same fallback useNodes/useCosts use.
    refetchInterval: 30_000,
  });

/** GET /projects/:id/map. Null until the map worker has built one. Same shape as core's CodebaseMap. */
export type CodebaseMapDTO = CodebaseMap;

export const useWorkers = (projectId: string) =>
  useQuery({ queryKey: keys.workers(projectId), queryFn: () => api<WorkerSettingDTO[]>("GET", `/projects/${projectId}/workers`), enabled: !!projectId });
export const useCodebaseMap = (projectId: string) =>
  useQuery({ queryKey: keys.map(projectId), queryFn: () => api<CodebaseMapDTO | null>("GET", `/projects/${projectId}/map`), enabled: !!projectId });

/**
 * The switch flips before the PUT returns, and the mutation stays pending until this project's
 * workers and map queries have refetched. A config write that leaves the worker off keeps
 * `disabledReason`; turning it on, or turning it off by hand, clears the notice.
 */
export function applyWorkerToggle(rows: readonly WorkerSettingDTO[] | undefined, worker: string, enabled: boolean): WorkerSettingDTO[] | undefined {
  return rows?.map((w) => (w.worker === worker ? { ...w, enabled, disabledReason: enabled || w.enabled ? null : w.disabledReason } : w));
}

export const useSetWorker = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (i: { worker: string; enabled: boolean; config?: Record<string, unknown> }) => {
      const saved = await api<WorkerSettingDTO>("PUT", `/projects/${projectId}/workers/${i.worker}`, {
        enabled: i.enabled,
        ...(i.config ? { config: i.config } : {}),
      });
      qc.setQueryData<WorkerSettingDTO[]>(keys.workers(projectId), (rows) => (rows ? rows.map((w) => (w.worker === saved.worker ? saved : w)) : [saved]));
      // Pending until the refetch lands, so the switch is not clickable against a stale row.
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.workers(projectId) }).catch(() => undefined),
        qc.invalidateQueries({ queryKey: keys.map(projectId) }).catch(() => undefined),
      ]);
      return saved;
    },
    onMutate: async (i) => {
      await qc.cancelQueries({ queryKey: keys.workers(projectId) });
      const prev = qc.getQueryData<WorkerSettingDTO[]>(keys.workers(projectId));
      qc.setQueryData(keys.workers(projectId), applyWorkerToggle(prev, i.worker, i.enabled));
      return { prev };
    },
    onError: (_err, _i, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.workers(projectId), ctx.prev);
    },
  });
};

export const useActors = () => useQuery({ queryKey: keys.actors, queryFn: () => api<ActorDTO[]>("GET", "/actors"), staleTime: 30_000 });
export type ProjectWithSummary = ProjectDTO & { summary: ProjectSummary };
/** Under the "projects" root, so the live "projects" invalidation refreshes it too. */
export const useProjectsStatus = () =>
  useQuery({ queryKey: [...keys.projects, "status"], queryFn: () => api<ProjectWithSummary[]>("GET", "/projects?include=status") });
/** `archivedAt` arrives with the hub's project archive (#1432); null or absent means active. */
export type ArchivableProject = { archivedAt?: number | null };
export const isArchived = (p: ArchivableProject | null | undefined): boolean => p?.archivedAt != null;
/** Archived projects only: the hub lists them on request (`includeArchived=1`) and the page keeps the archived ones. */
export const useArchivedProjects = () =>
  useQuery({
    queryKey: [...keys.projects, "status", "archived"],
    queryFn: async () => (await api<(ProjectWithSummary & ArchivableProject)[]>("GET", "/projects?include=status&includeArchived=1")).filter(isArchived),
  });
/** Archive or unarchive (admin only). Refreshes every list the hub filters: projects, overview, Bridge, Inbox. */
export const useSetProjectArchived = () =>
  useWrite(
    (i: { id: string; archived: boolean }) => api<ProjectDTO>("POST", `/projects/${i.id}/${i.archived ? "archive" : "unarchive"}`),
    [keys.projects, keys.overview, keys.bridge, ["project"], ["decisions"]],
  );
export const useProjects = () => useQuery({ queryKey: keys.projects, queryFn: () => api<ProjectDTO[]>("GET", "/projects") });
export const useProject = (id: string) =>
  useQuery({
    queryKey: keys.project(id),
    queryFn: () => api<{ status: ProjectStatus; tasks: TaskDTO[]; intents: IntentDTO[] }>("GET", `/projects/${id}`),
    enabled: !!id,
  });
/** When the project was archived (null = active or still loading). Shares the project query, so every page of a project gates on one fetch. */
export const useProjectArchivedAt = (projectId: string): number | null =>
  (useProject(projectId).data?.status.project as ArchivableProject | undefined)?.archivedAt ?? null;
/** True once the hub refuses new work here (409 project_archived): pages hide or disable those actions. */
export const useProjectArchived = (projectId: string): boolean => useProjectArchivedAt(projectId) !== null;
export const useTask = (id: string) => useQuery({ queryKey: keys.task(id), queryFn: () => api<TaskDetail>("GET", `/tasks/${id}`) });
/** Project rows do not carry abandonment audit data; hydrate only abandoned task details so their stored reasons remain visible after they age out of the event feed. */
export const useAbandonmentEvents = (tasks: readonly TaskDTO[]) =>
  useQueries({
    queries: tasks
      .filter((task) => task.mergeState === "abandoned")
      .map((task) => ({ queryKey: keys.task(task.id), queryFn: () => api<TaskDetail>("GET", `/tasks/${task.id}`) })),
  }).flatMap((query) => query.data?.timeline.filter((event) => event.type === "task.abandoned") ?? []);
export const useIntent = (id: string, enabled = true) =>
  useQuery({
    enabled: enabled && !!id,
    queryKey: keys.intent(id),
    queryFn: () => api<{ intent: IntentDTO; tasks: TaskDTO[]; decisions: DecisionDTO[] }>("GET", `/intents/${id}`),
  });
export const useInbox = () =>
  useQuery({ queryKey: keys.decisions("inbox"), queryFn: () => api<DecisionDTO[]>("GET", "/decisions?assigneeId=me&status=open") });
export const useOpenDecisions = () =>
  useQuery({ queryKey: keys.decisions("open"), queryFn: () => api<DecisionDTO[]>("GET", "/decisions?status=open") });
/** One decision by id, open or answered (the run transcript's guard links point here). */
export const useDecision = (id: string) =>
  useQuery({ queryKey: keys.decision(id), queryFn: () => api<DecisionDTO>("GET", `/decisions/${id}`), enabled: !!id });
export const useRecentAnswers = () =>
  useQuery({ queryKey: keys.decisions("mine-answered"), queryFn: () => api<DecisionDTO[]>("GET", "/decisions?assigneeId=me&status=answered") });
export const useRules = () => useQuery({ queryKey: keys.rules, queryFn: () => api<RuleDTO[]>("GET", "/rules") });
export const useEvents = (projectId?: string, limit = 60, enabled = true) =>
  useQuery({
    queryKey: [...keys.events(projectId), limit],
    queryFn: () => api<EventDTO[]>("GET", `/events?limit=${limit}${projectId ? `&projectId=${projectId}` : ""}`),
    enabled,
  });
/** The status page's report (docs/specs/status-page.md §4.5). Live: live.tsx invalidates the `status` root, so no polling. */
export const useStatusReport = (projectId: string | null) =>
  useQuery({
    queryKey: ["status", projectId ?? ""] as const,
    queryFn: () => api<StatusReport>("GET", `/projects/${projectId}/status-report`),
    enabled: !!projectId,
  });
export const useDeliveryPipeline = (projectId: string | null, days = 7) =>
  useQuery({
    queryKey: ["status", projectId ?? "", "delivery-pipeline", days] as const,
    queryFn: () => api<DeliveryPipelineReport>("GET", `/projects/${projectId}/delivery-pipeline?days=${days}`),
    enabled: !!projectId,
  });
/** The same report as Markdown, for "Copy as Markdown". */
export async function fetchStatusMarkdown(projectId: string, token = getToken()): Promise<string> {
  const res = await fetch(`/api/projects/${projectId}/status-report.md`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, "error", res.statusText || "Could not load the Markdown export");
  return res.text();
}
export const useMetrics = () => useQuery({ queryKey: keys.metrics, queryFn: () => api<Metrics>("GET", "/metrics") });
export const useRuns = (query: string, enabled = true) => useQuery({ queryKey: keys.runs(query), queryFn: () => api<RunDTO[]>("GET", `/runs?${query}`), enabled });
export const useTasks = () => useQuery({ queryKey: keys.tasks, queryFn: () => api<TaskDTO[]>("GET", "/tasks") });
/** The project's command-guard rows, newest first (docs/specs/command-guard.md §4.5 Guard tab). */
export const useGuardLog = (projectId: string, filters: GuardFilters) =>
  useQuery({
    queryKey: keys.guard(projectId, filters),
    queryFn: () => api<GuardDecisionDTO[]>("GET", withQuery(`/projects/${projectId}/guard`, guardQuery(filters))),
  });
export const useThreads = (filters: ThreadFilters = {}) =>
  useQuery({ queryKey: keys.threads(filters), queryFn: () => api<ThreadDTO[]>("GET", withQuery("/threads", filters)) });
export const useMessages = (filters: MessageFilters) =>
  useQuery({
    queryKey: keys.messages(filters),
    queryFn: () => api<MessageDTO[]>("GET", withQuery("/messages", filters)),
    enabled: !!(filters.threadId || filters.taskId),
  });
export const useRun = (id: string) =>
  useQuery({ queryKey: keys.run(id), queryFn: () => api<{ run: RunDTO; events: RunEventDTO[] }>("GET", `/runs/${id}`), staleTime: 2_000 });
export const useIdeas = (projectId?: string, enabled = true) =>
  useQuery({
    enabled,
    queryKey: ["intent", "ideas", projectId ?? "all"],
    queryFn: () => api<IdeaRowDTO[]>("GET", projectId ? `/ideas?projectId=${encodeURIComponent(projectId)}` : "/ideas"),
    refetchInterval: 20_000,
  });
export const useNodes = () => useQuery({ queryKey: keys.nodes, queryFn: () => api<NodeDTO[]>("GET", "/nodes"), refetchInterval: 30_000 });
/** The node re-probes after the POST and reports with its next heartbeat, so an immediate refetch still shows the old state. */
export const SERVICE_CHECK_REFETCH_MS = [2_000, 6_000, 15_000];
/** Refetches `key` once now and again after each delay; returns a function that cancels the pending ones. */
export function refetchAfterDelays(qc: Pick<QueryClient, "invalidateQueries">, key: QueryKey, delays: number[] = SERVICE_CHECK_REFETCH_MS): () => void {
  const refetch = () => void qc.invalidateQueries({ queryKey: key });
  refetch();
  const timers = delays.map((ms) => setTimeout(refetch, ms));
  return () => timers.forEach(clearTimeout);
}
/** "Check again" on a node's Connected services (machine-connected-services §8): asks the node to re-probe now; the new state arrives with its next heartbeat, so the list is refetched a few times. */
export const useServiceCheck = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nodeId: string) => api<{ ok: true }>("POST", `/nodes/${nodeId}/service-check`),
    onSuccess: () => {
      refetchAfterDelays(qc, keys.nodes);
    },
  });
};
/** Which process served this page. It can't change within a session, so it is fetched once. */
export const useWhere = () =>
  useQuery({ queryKey: keys.where, queryFn: () => api<WhereInfo>("GET", "/where"), staleTime: Infinity, retry: false });
export const useCosts = (days = 7) => useQuery({ queryKey: [...keys.costs, days], queryFn: () => api<CostSummary>("GET", `/costs?days=${days}`), refetchInterval: 30_000 });
/**
 * A hub without the lesson routes answers an unknown path with its app shell (200, HTML), not a 404.
 * Reading that as "no lessons" would hide the problem, so a reply without a `lessons` array is
 * reported as a 404, which the pages treat as "this hub has no lessons yet".
 */
async function fetchLessons(path: string): Promise<LessonDTO[]> {
  const reply = await api<{ lessons?: LessonDTO[] } | null>("GET", path);
  if (!reply || !Array.isArray(reply.lessons)) throw new ApiError(404, "not_found", "This hub has no lesson routes yet");
  return reply.lessons;
}

// A missing route will not appear on retry, so a 404 is not retried.
const retryUnlessMissing = (failures: number, error: Error) => !(error instanceof ApiError && error.status === 404) && failures < 3;

/** Every lesson of a project, active and retired; the page splits them. */
export const useProjectLessons = (projectId: string) =>
  useQuery({
    queryKey: keys.projectLessons(projectId),
    queryFn: () => fetchLessons(`/projects/${projectId}/lessons?status=all`),
    enabled: !!projectId,
    retry: retryUnlessMissing,
  });
/** The lessons a run was given (its lesson_injections rows), not the whole project's. */
export const useRunLessons = (runId: string) =>
  useQuery({ queryKey: keys.runLessons(runId), queryFn: () => fetchLessons(`/runs/${runId}/lessons`), enabled: !!runId, retry: retryUnlessMissing });
/** The rules an agent learned from corrections, with their source decisions and repeat counts (spec §2.5). */
export const useAgentCorrectionRules = (agentId: string, enabled = true) =>
  useQuery({
    queryKey: keys.correctionRules(agentId),
    queryFn: async () => {
      const reply = await api<{ rules?: CorrectionRuleDTO[] } | null>("GET", `/agents/${agentId}/correction-rules`);
      if (!reply || !Array.isArray(reply.rules)) throw new ApiError(404, "not_found", "This hub has no correction routes yet");
      return reply.rules;
    },
    enabled: !!agentId && enabled,
    retry: retryUnlessMissing,
  });
/** Every correction of an agent, including the ones that did not become a rule. People only. */
export const useAgentCorrections = (agentId: string, enabled = true) =>
  useQuery({
    queryKey: keys.agentCorrections(agentId),
    queryFn: async () => {
      const reply = await api<{ corrections?: CorrectionDTO[] } | null>("GET", `/agents/${agentId}/corrections`);
      if (!reply || !Array.isArray(reply.corrections)) throw new ApiError(404, "not_found", "This hub has no correction routes yet");
      return reply.corrections;
    },
    enabled: !!agentId && enabled,
    retry: retryUnlessMissing,
  });
/**
 * Retires or restores one lesson. The reply is the lesson as it now is, so every cached list that
 * holds it is patched in place: the row moves between sections with no refetch.
 */
export const useSetLessonRetired = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (i: { id: string; retire: boolean }) => api<LessonDTO>("POST", `/lessons/${i.id}/${i.retire ? "retire" : "restore"}`),
    onSuccess: (lesson) => {
      qc.setQueriesData<LessonDTO[]>({ queryKey: ["lessons"] }, (list) => list?.map((l) => (l.id === lesson.id ? lesson : l)));
      patchRules(qc, lesson);
    },
  });
};
/** Pins an active lesson so it is live at once (spec §2.6 probation). The reply patches the cached lists like retire does. */
export const usePinLesson = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<LessonDTO>("POST", `/lessons/${id}/pin`),
    onSuccess: (lesson) => {
      qc.setQueriesData<LessonDTO[]>({ queryKey: ["lessons"] }, (list) => list?.map((l) => (l.id === lesson.id ? lesson : l)));
      patchRules(qc, lesson);
    },
  });
};
/**
 * The profile's rules wrap the lesson with counts: the lesson is patched at once, so the buttons
 * never act on stale state, and the counts are refetched.
 */
function patchRules(qc: QueryClient, lesson: LessonDTO) {
  qc.setQueriesData<CorrectionRuleDTO[]>({ queryKey: ["corrections", "rules"] }, (rules) => patchRuleLesson(rules, lesson));
  void qc.invalidateQueries({ queryKey: ["corrections"] });
}

/** GET /routines: one row per routine, `paused_failing` first, then by `nextDueAt` (docs/specs/routines.md §3.1). */
export const useRoutines = (projectId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: keys.routines(projectId ?? "all"),
    queryFn: () => api<RoutineListDTO[]>("GET", projectId ? `/routines?projectId=${encodeURIComponent(projectId)}` : "/routines"),
    enabled,
    retry: retryUnlessMissing,
  });
/** `fresh` makes the query refetch on every mount (the edit form must not start from a cached copy), so isFetchedAfterMount always turns true. */
export const useRoutine = (id: string, fresh = false) =>
  useQuery({ queryKey: keys.routine(id), queryFn: () => api<RoutineDTO>("GET", `/routines/${id}`), enabled: !!id, retry: retryUnlessMissing, refetchOnMount: fresh ? "always" : true });
export const useRoutineRuns = (id: string) =>
  useQuery({ queryKey: keys.routineRuns(id), queryFn: () => api<RoutineRunDTO[]>("GET", `/routines/${id}/runs`), enabled: !!id, retry: retryUnlessMissing });
/** Run now, Pause and Resume. The hub enforces who may (Q14); a refusal surfaces as the mutation error. */
export const useRoutineAction = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: "run" | "pause" | "resume") => api<unknown>("POST", `/routines/${id}/${action}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["routines"] }),
  });
};

/** Create (no id) or edit (id) a routine. The reply is the routine as saved; the hub's refusals (400 fence, 403 role) surface as the mutation error. */
export const useSaveRoutine = (id?: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => (id ? api<RoutineDTO>("PATCH", `/routines/${id}`, body) : api<RoutineDTO>("POST", "/routines", body)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["routines"] }),
  });
};

/**
 * A remote agent's sandbox (docs/specs/remote-runtime.md §11 T9). Null when the hub has no such route
 * (404): the panel then stays hidden rather than showing invented state.
 */
export async function fetchSandboxStatus(actorId: string): Promise<SandboxStatusDTO | null> {
  try {
    return await api<SandboxStatusDTO>("GET", `/actors/${actorId}/sandbox`);
  } catch (e) {
    // 404: not a remote agent. 403: the route is for an admin, the agent or its owner; others see no panel.
    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null;
    throw e;
  }
}
export const useSandboxStatus = (actorId: string, enabled = true) =>
  useQuery({
    queryKey: ["sandbox", actorId] as const,
    queryFn: () => fetchSandboxStatus(actorId),
    enabled,
    refetchInterval: 15_000,
    retry: false,
  });
/** Sandbox actions (`resume`, `check-login`) reply with the fresh status, which replaces the cached one. */
export const useSandboxAction = (actorId: string, action: "resume" | "check-login") => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<SandboxStatusDTO>("POST", `/actors/${actorId}/sandbox/${action}`),
    onSuccess: (status) => qc.setQueryData(["sandbox", actorId], status),
  });
};
/** The agent's active runs, polled so a stop request shows as stopping and then stopped. */
export const useActiveAgentRuns = (agentId: string, enabled = true) =>
  useQuery({ queryKey: ["runs", `agent-${agentId}`] as const, queryFn: () => api<RunDTO[]>("GET", `/runs?agentId=${agentId}&limit=30`), enabled,
    // The live socket invalidates this on run events; poll only while a stop is in flight, to catch its end.
    refetchInterval: (q) => (q.state.data?.some((r) => r.host === "vercel-sandbox" && r.stopRequested && (r.status === "starting" || r.status === "running")) ? 3_000 : false),
  });

export const useStopRun = () => useWrite((id: string) => api<RunDTO>("POST", `/runs/${id}/stop`), [["run"], ["runs"], ["task"], ["bridge"], ["mission"], ["sandbox"]]);
/** POST /runs/:id/abandon: human admin only; closes a ghost run with a recorded reason (10-4000 chars). 409 run_finished when it already ended. */
export const abandonRun = (id: string, reason: string) => api<RunDTO>("POST", `/runs/${id}/abandon`, { reason });
export const useAbandonRun = () =>
  useWrite((i: { id: string; reason: string }) => abandonRun(i.id, i.reason), [["run"], ["runs"], ["task"], ["bridge"], ["mission"], ["sandbox"]]);
/** GET /chief/actions (chief-of-office.md §8.3). Under the "chief" root so `chief.*` live events refresh it. */
export const useChiefActions = (filters: ChiefActionFilters & { limit?: number } = {}, enabled = true) =>
  useQuery({
    queryKey: ["chief", "actions", filters] as const,
    queryFn: () => api<ChiefActionDTO[]>("GET", withQuery("/chief/actions", chiefQuery(filters))),
    enabled,
    retry: false,
  });
/** Today's Chief acts, paged with `before` so a busy day is counted whole. Under the "chief" root like the feed. */
export const useChiefToday = () => {
  const from = startOfDay(Date.now());
  return useQuery({
    queryKey: ["chief", "today", from] as const,
    queryFn: () => chiefActionsSince(from, (before) => api<ChiefActionDTO[]>("GET", withQuery("/chief/actions", chiefQuery({ limit: CHIEF_PAGE, before })))),
    retry: false,
    placeholderData: keepPreviousData, // the key rolls over at midnight; keep the tile up while it refetches
  });
};
/** POST /chief/actions/:id/undo: people only. A 409 carries what changed since. */
export const useUndoChiefAction = () => {
  const qc = useQueryClient();
  const write = useWrite((id: string) => api<ChiefActionDTO>("POST", `/chief/actions/${id}/undo`), [["chief"], ["task"], ["bridge"]]);
  // A 409 (changed since, already undone) means the row on screen is stale: refetch so it shows its real state.
  return { ...write, mutate: (id: string) => write.mutate(id, { onError: () => void qc.invalidateQueries({ queryKey: ["chief"] }) }) };
};
export const useSendNote = () =>
  useWrite((i: { id: string; text: string }) => api<RunDTO>("POST", `/runs/${i.id}/notes`, { text: i.text }), [["run"], ["runs"], ["task"], ["bridge"]]);
export const useAgentConfig = () =>
  useWrite((i: { id: string; agentConfig: Partial<AgentConfig> }) => api<ActorDTO>("PATCH", `/actors/${i.id}`, { agentConfig: i.agentConfig }), [["actors"]]);
export const useActorRuntime = () =>
  useWrite((i: { id: string; runtime: Runtime }) => api<ActorDTO>("PATCH", `/actors/${i.id}`, { runtime: i.runtime }), [["actors"]]);
export const useSetDelegationPolicy = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (i: { id: string; policy: DelegationPolicy | null }) => api<ActorDTO>("PATCH", `/actors/${i.id}`, { delegationPolicy: i.policy }),
    onSuccess: (updated) => {
      // Re-render from the response directly rather than waiting on a refetch: the PATCH's own
      // reply is the canonical row.
      qc.setQueryData<ActorDTO[]>(keys.actors, (list) => list?.map((a) => (a.id === updated.id ? updated : a)));
      qc.setQueryData(keys.me, (me: ActorDTO | undefined) => (me?.id === updated.id ? updated : me));
    },
  });
};
export const useAgentImportPreview = (id: string) =>
  useQuery({
    queryKey: keys.agentImport(id),
    queryFn: () => (id === MOCK_IMPORT_PREVIEW.id ? Promise.resolve(MOCK_IMPORT_PREVIEW) : api<ImportPreviewDTO>("GET", `/agent-imports/${id}`)),
    enabled: !!id,
  });
export const useCreateAgentImportPreview = () =>
  useWrite((i: { source: ImportSource; mainAgent?: string; actorId?: string }) => api<ImportPreviewDTO>("POST", "/agent-imports/preview", i), [["agent-import"]]);
export const useApplyAgentImport = () =>
  useWrite(
    (i: { id: string; name: string; ownerId: string; allowedAreas: string[]; skills: string[]; maxParallel: number; excludedFiles?: string[]; approve: string[] }) =>
      api<ActorDTO>("POST", `/agent-imports/${i.id}/apply`, {
        name: i.name,
        ownerId: i.ownerId,
        allowedAreas: i.allowedAreas,
        skills: i.skills,
        maxParallel: i.maxParallel,
        excludedFiles: i.excludedFiles ?? [],
        approve: i.approve,
      }),
    [["actors"], ["agent-imports"]],
  );
export const useAgentImports = (actorId: string) =>
  useQuery({ queryKey: keys.agentImports(actorId), queryFn: () => api<AgentImportDTO[]>("GET", `/actors/${actorId}/imports`), enabled: !!actorId });
export const useRollbackAgentImport = () =>
  useWrite((i: { importId: string; actorId: string }) => api<ActorDTO>("POST", `/agent-imports/${i.importId}/rollback`), [["actors"], ["agent-imports"]]);

// Skills library. Under "skills" so one invalidation refreshes the list, a skill and its versions.
export const useSkills = () => useQuery({ queryKey: ["skills", "list"], queryFn: () => api<SkillListItemDTO[]>("GET", "/skills") });
export const useSkill = (id: string) =>
  useQuery({ queryKey: ["skills", "one", id], queryFn: () => api<SkillDetailDTO>("GET", `/skills/${id}`), enabled: !!id });
export const useSkillVersion = (id: string) =>
  useQuery({ queryKey: ["skills", "version", id], queryFn: () => api<SkillVersionDTO>("GET", `/skill-versions/${id}`), enabled: !!id });
/** Adds from a source URL (JSON) or an uploaded zip. `folders` picks SKILL.md folders when a source holds several. */
export const useFetchSkills = () =>
  useWrite(async (i: { source?: string; zip?: File; folders?: string[] }) => {
    if (!i.zip) return api<SkillFetchResult>("POST", "/skills/fetch", { source: i.source, folders: i.folders });
    const form = new FormData();
    form.append("file", i.zip);
    if (i.folders?.length) form.append("folders", JSON.stringify(i.folders));
    return api<SkillFetchResult>("POST", "/skills/fetch", form);
  }, [["skills"]]);
export const useApproveSkillVersion = () =>
  useWrite(
    (i: { id: string; serviceHosts: string[]; acknowledgedFindingIds: string[]; hookVerdicts: HookVerdicts }) =>
      api<unknown>("POST", `/skill-versions/${i.id}/approve`, { serviceHosts: i.serviceHosts, acknowledgedFindingIds: i.acknowledgedFindingIds, hookVerdicts: i.hookVerdicts }),
    [["skills"], ["decisions"]],
  );
export const useRejectSkillVersion = () =>
  useWrite((i: { id: string; reason: string }) => api<unknown>("POST", `/skill-versions/${i.id}/reject`, { reason: i.reason }), [["skills"]]);
export const useRefetchSkill = () => useWrite((id: string) => api<SkillFetchResult>("POST", `/skills/${id}/refetch`), [["skills"]]);
export const useArchiveSkill = () => useWrite((id: string) => api<unknown>("POST", `/skills/${id}/archive`), [["skills"]]);
/** The skills assigned to one agent (GET /agents/:id/skills). Under "skills", so it refreshes with the Skills page picker. */
export const useAgentSkills = (agentId: string) =>
  useQuery({ queryKey: ["skills", "agent", agentId], queryFn: () => api<{ agentId: string; skillIds: string[]; skills: SkillListItemDTO[] }>("GET", `/agents/${agentId}/skills`), enabled: !!agentId });
export const useSetAgentSkills = () =>
  useWrite((i: { id: string; skillIds: string[] }) => api<unknown>("PUT", `/agents/${i.id}/skills`, { skillIds: i.skillIds }), [["skills"]]);
export const useSetSkillAgents = () =>
  useWrite((i: { id: string; agentIds: string[] }) => api<unknown>("PUT", `/skills/${i.id}/agents`, { agentIds: i.agentIds }), [["skills"]]);

export const useSendMessage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { replyTo: string; body: string }) => api<MessageDTO>("POST", "/messages", input),
    onSuccess: (message) => {
      // The POST response is the canonical row. Put it in the open conversation immediately;
      // the websocket/refetch that follows reconciles counts and any concurrent replies.
      qc.setQueryData<MessageDTO[]>(keys.messages({ threadId: message.threadId }), (current) =>
        current?.some((item) => item.id === message.id) ? current : [message, ...(current ?? [])],
      );
      void qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
};

/**
 * The Chief's state for Roomie's hand-off (docs/specs/chief-of-office.md C1-AC4). Null when this hub has
 * no Chief or no such route (a 404). Any other failure (5xx, network, 401) throws, so React Query keeps
 * the previous state instead of wiping it.
 */
export type ChiefRead = { state?: string; name?: string; reason?: string | null };
export async function readChief(call: () => Promise<ChiefRead | null> = () => api<ChiefRead | null>("GET", "/office/chief")): Promise<ChiefRead | null> {
  try {
    const raw = await call();
    return raw && typeof raw === "object" ? raw : null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}
export const useChief = () =>
  useQuery({
    queryKey: ["office-chief"] as const,
    queryFn: () => readChief(),
    refetchInterval: 30_000,
    retry: false,
  });

/** Why the breaker is open (`GET /office/chief/guards`), for the paused banner. Only asked while paused; a refusal just means no reason is shown. */
export const useChiefPausedReason = (enabled: boolean) =>
  useQuery({
    queryKey: ["office-chief", "guards"] as const,
    queryFn: async () => {
      try {
        return (await api<{ pausedReason: string | null }>("GET", "/office/chief/guards")).pausedReason ?? null;
      } catch {
        return null;
      }
    },
    enabled,
    retry: false,
  });

/**
 * Posts a person's text into the Chief thread (spec §5.2): an ordinary message that expects a reply.
 * With a threadId it replies inside that thread; without one it opens a new thread. Resolves once the
 * thread list is fresh, so the caller can open the thread straight away.
 */
export const useAskChief = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; to: string; body: string; threadId?: string | null }) =>
      api<MessageDTO>(
        "POST",
        "/messages",
        input.threadId
          ? { replyTo: input.threadId, body: input.body, expectsReply: true }
          : { projectId: input.projectId, to: input.to, body: input.body, expectsReply: true },
      ),
    // "all": the Conversations list may be mounted nowhere yet, and it must not open on a stale list.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["threads"], refetchType: "all" }),
  });
};

export const useReadMessages = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; threadId: string }) => api<{ marked: number }>("POST", "/messages/read", { ids: input.ids }),
    onMutate: ({ ids, threadId }) => {
      const read = new Set(ids);
      const at = Date.now();
      qc.setQueryData<MessageDTO[]>(keys.messages({ threadId }), (current) =>
        current?.map((message) => (read.has(message.id) ? { ...message, readAt: at } : message)),
      );
      qc.setQueriesData<ThreadDTO[]>({ queryKey: ["threads"] }, (current) =>
        current?.map((thread) => (thread.threadId === threadId ? { ...thread, unreadCount: 0 } : thread)),
      );
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: ["messages"] });
      void qc.invalidateQueries({ queryKey: ["threads"] });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messages"] });
      void qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
};

function useWrite<I, O>(fn: (input: I) => Promise<O>, invalidate: QueryKey[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of invalidate) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export const useAnswer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (i: { id: string; optionId: string; rationale?: string }) =>
      api<DecisionDTO>("POST", `/decisions/${i.id}/answer`, { optionId: i.optionId, rationale: i.rationale || null }),
    onSuccess: (answered) => {
      // Drop the ticket right away so the next decision is on screen without waiting for a refetch.
      qc.setQueryData<DecisionDTO[]>(keys.decisions("inbox"), (list) => list?.filter((d) => d.id !== answered.id));
      // Same on Home: the bridge can take seconds to refetch, and until then the answered card stayed up
      // with live buttons, so a second click looked necessary (and silently hit "already answered").
      qc.setQueriesData<BridgeView>({ queryKey: keys.bridge }, (view) => {
        if (!view) return view;
        const drop = (list: DecisionDTO[]) => list.filter((d) => d.id !== answered.id);
        const n = view.needsAHuman;
        return { ...view, needsAHuman: { ...n, approve: drop(n.approve), advise: drop(n.advise), act: drop(n.act) } };
      });
      qc.setQueryData(keys.decision(answered.id), answered);
      for (const key of [["decisions"], ["overview"], ["task"], ["project"], ["bridge"], ["mission"]]) void qc.invalidateQueries({ queryKey: key });
    },
  });
};

export const useAssign = () =>
  useWrite((i: { id: string; mode: Mode; builderId: string | null; reviewerIds: string[]; reason?: string }) =>
    api<TaskDTO>("POST", `/tasks/${i.id}/assign`, { mode: i.mode, builderId: i.builderId, reviewerIds: i.reviewerIds, reason: i.reason || null }),
  [["task"], ["tasks"], ["project"]]);

export const useRoute = () => useWrite((id: string) => api<TaskDTO>("POST", `/tasks/${id}/route`), [["task"], ["tasks"], ["project"]]);
export const useTransition = () =>
  useWrite((i: { id: string; to: Stage; note?: string }) => api<TaskDTO>("POST", `/tasks/${i.id}/transition`, { to: i.to, note: i.note ?? null }), [
    ["task"],
    ["tasks"],
    ["project"],
  ]);
export const abandonMergeTask = (id: string, reason: string) => api<TaskDTO>("POST", `/tasks/${id}/abandon`, { reason });
export const useAbandonMerge = () =>
  useWrite((i: { id: string; reason: string }) => abandonMergeTask(i.id, i.reason), [["task"], ["tasks"], ["project"], ["bridge"], ["events"], ["status"], ["decisions"], ["runs"]]);
export const useClaim = () =>
  useWrite((id: string) => api<{ status: string; reason?: string }>("POST", `/tasks/${id}/claim`), [["task"], ["tasks"], ["project"]]);

export const useCreate = <T,>(path: string, invalidate: QueryKey[]) => useWrite((body: unknown) => api<T>("POST", path, body), invalidate);
export const usePatch = <T,>(invalidate: QueryKey[]) =>
  useWrite((i: { path: string; body: unknown }) => api<T>("PATCH", i.path, i.body), invalidate);
export const usePost = <T,>(invalidate: QueryKey[]) =>
  useWrite((i: { path: string; body?: unknown }) => api<T>("POST", i.path, i.body), invalidate);
export const useDelete = (invalidate: QueryKey[]) => useWrite((path: string) => api<void>("DELETE", path), invalidate);

